import type { JourneyPhase } from '@/types/brand'
import type { SearchIntent } from '@/types/search-intent'
import {
  RUN_TIMED_OUT_ERROR_CODE,
  isRunWaiting,
  type RunEntry,
  type SetupRun,
} from '@/types/setup-run'

export const PLAN_RUN_STAGES = [
  'inventory',
  'content',
  'research',
  'competitors',
  'demand',
  'relevance',
  'topic-candidates',
  'review-topics',
  'fill',
  'serp',
  'grouping',
  'type-stage',
  'placement',
  'decision-pages',
  'site-match',
  'review-plan',
  'commit',
] as const

export type PlanRunStage = (typeof PLAN_RUN_STAGES)[number]

/** The stages that pause a run until a person answers. */
export const GATE_STAGES = ['review-topics', 'review-plan'] as const

export type PlanGateStage = (typeof GATE_STAGES)[number]

/** The job a page does in a topic, in tree order: the explainer first, then the four pages that link back to it. */
export const PAGE_TYPES = ['explainer', 'offer', 'comparison', 'use-case', 'supporting'] as const

export type PageType = (typeof PAGE_TYPES)[number]

/** True when `value` is one of the five page types. */
export function isPageType(value: unknown): value is PageType {
  return typeof value === 'string' && (PAGE_TYPES as readonly string[]).includes(value)
}

export interface InventoryCounts {
  readonly pages: number
  readonly fromSitemap: number
  readonly ranking: number
  readonly readable: number
  readonly gone: number
}

export interface ContentCounts {
  readonly pages: number
  readonly read: number
  readonly unchanged: number
}

export interface ResearchCounts {
  readonly coverage: number
  readonly terms: number
}

export interface CompetitorsCounts {
  readonly competitors: number
  readonly searches: number
}

export interface DemandCounts {
  readonly terms: number
  readonly measured: number
  readonly units: number
}

export interface RelevanceCounts {
  readonly units: number
  readonly scored: number
  readonly kept: number
}

export interface SerpCounts {
  readonly keywords: number
  readonly ordered: number
  readonly received: number
  readonly missing: number
  readonly clusters: number
}

export interface GroupingCounts {
  readonly keywords: number
  readonly pages: number
}

export interface TypeStageCounts {
  readonly pages: number
  readonly kept: number
  readonly dropped: number
}

export interface TopicCandidatesCounts {
  readonly candidates: number
}

export interface ReviewTopicsCounts {
  readonly kept: number
  readonly added: number
}

export interface FillCounts {
  readonly topics: number
  readonly units: number
}

export interface PlacementCounts {
  readonly pages: number
  readonly topics: number
  readonly dropped: number
}

export interface ReviewPlanCounts {
  readonly removed: number
}

export interface DecisionPagesCounts {
  readonly pages: number
  readonly objectives: number
}

export interface SiteMatchCounts {
  readonly planned: number
  readonly existing: number
  readonly excluded: number
  readonly byRanking: number
  readonly byModel: number
  readonly update: number
}

export interface CommitCounts {
  readonly topics: number
  readonly pages: number
  readonly keywords: number
  readonly removed: number
}

export interface RunStageEntryBase extends RunEntry {
  readonly seq: number
  readonly deadlineAt?: string
  readonly outputPath?: string
}

export interface InventoryStageEntry extends RunStageEntryBase {
  readonly id: 'inventory'
  readonly counts: InventoryCounts
}

export interface ContentStageEntry extends RunStageEntryBase {
  readonly id: 'content'
  readonly counts: ContentCounts
}

export interface ResearchStageEntry extends RunStageEntryBase {
  readonly id: 'research'
  readonly counts: ResearchCounts
}

export interface CompetitorsStageEntry extends RunStageEntryBase {
  readonly id: 'competitors'
  readonly counts: CompetitorsCounts
}

export interface DemandStageEntry extends RunStageEntryBase {
  readonly id: 'demand'
  readonly counts: DemandCounts
}

export interface RelevanceStageEntry extends RunStageEntryBase {
  readonly id: 'relevance'
  readonly counts: RelevanceCounts
}

export interface SerpStageEntry extends RunStageEntryBase {
  readonly id: 'serp'
  readonly counts: SerpCounts
}

export interface GroupingStageEntry extends RunStageEntryBase {
  readonly id: 'grouping'
  readonly counts: GroupingCounts
}

export interface TypeStageStageEntry extends RunStageEntryBase {
  readonly id: 'type-stage'
  readonly counts: TypeStageCounts
}

export interface TopicCandidatesStageEntry extends RunStageEntryBase {
  readonly id: 'topic-candidates'
  readonly counts: TopicCandidatesCounts
}

export interface ReviewTopicsStageEntry extends RunStageEntryBase {
  readonly id: 'review-topics'
  readonly counts: ReviewTopicsCounts
}

export interface FillStageEntry extends RunStageEntryBase {
  readonly id: 'fill'
  readonly counts: FillCounts
}

export interface PlacementStageEntry extends RunStageEntryBase {
  readonly id: 'placement'
  readonly counts: PlacementCounts
}

export interface ReviewPlanStageEntry extends RunStageEntryBase {
  readonly id: 'review-plan'
  readonly counts: ReviewPlanCounts
}

export interface DecisionPagesStageEntry extends RunStageEntryBase {
  readonly id: 'decision-pages'
  readonly counts: DecisionPagesCounts
}

export interface SiteMatchStageEntry extends RunStageEntryBase {
  readonly id: 'site-match'
  readonly counts: SiteMatchCounts
}

export interface CommitStageEntry extends RunStageEntryBase {
  readonly id: 'commit'
  readonly counts: CommitCounts
}

/** One stage of a plan run, its counts typed by the stage. */
export type PlanRunStageEntry =
  | InventoryStageEntry
  | ContentStageEntry
  | ResearchStageEntry
  | CompetitorsStageEntry
  | DemandStageEntry
  | RelevanceStageEntry
  | TopicCandidatesStageEntry
  | ReviewTopicsStageEntry
  | FillStageEntry
  | SerpStageEntry
  | GroupingStageEntry
  | TypeStageStageEntry
  | PlacementStageEntry
  | DecisionPagesStageEntry
  | SiteMatchStageEntry
  | ReviewPlanStageEntry
  | CommitStageEntry

export const PLAN_RUN_ERROR_CODES = [
  'plan_run_enqueue_failed',
  'plan_run_in_progress',
  'plan_run_failed',
  'brand_domain_unknown',
  'brand_market_undecided',
  'plan_site_unreachable',
  'plan_raw_store_failed',
  'plan_stage_deadline_exceeded',
  'vendor_budget_exhausted',
  'vendor_not_configured',
  'vendor_http_error',
  'vendor_envelope_error',
  'web_content_fetch_failed',
  'plan_beliefs_missing',
  'brand_facts_missing',
  'plan_discovery_no_seeds',
  'model_output_invalid',
  'plan_relevance_nothing_kept',
  'plan_serp_no_results',
  'plan_no_pages_kept',
  'plan_focus_nothing_new',
  RUN_TIMED_OUT_ERROR_CODE,
] as const

export type PlanRunErrorCode = (typeof PLAN_RUN_ERROR_CODES)[number]

export const PLAN_RUN_STALE_AFTER_MS = 15 * 60 * 1000

/** The vendor market a plan run measures in. */
export interface PlanMarket {
  readonly locationCode: number
  readonly languageCode: string
}

/** What a person may give when they open a plan run: the topic to find more pages for, a steer note, a market for this run only. */
export interface StartPlanInput {
  readonly focusTopicId?: string
  readonly direction?: string
  readonly market?: PlanMarket
}

/** The longest steer note a plan run takes. */
export const PLAN_DIRECTION_MAX_LENGTH = 300

/** The one topic a focus run finds more pages for, and the subject its discovery researches. */
export interface PlanFocus {
  readonly topicId: string
  readonly subject: string
}

/** One plan run, `planRuns/{runId}`. */
export interface PlanRun extends SetupRun<PlanRunStage, PlanRunStageEntry> {
  readonly errorCode?: PlanRunErrorCode
  readonly startedByUserId: string
  /** The Content Strategy's `meta.confirmedAt` when the run started; `null` when it was not confirmed. */
  readonly strategyStamp: string | null
  readonly domain: string
  readonly market: PlanMarket
  /** The topic the run only adds pages to; `null` for a run that plans the whole brand. */
  readonly focus: PlanFocus | null
  /** The note that steered the run when it started; `null` when none was given. */
  readonly direction: string | null
  /** When the one reminder for a run waiting at a gate was sent; `null` until then. */
  readonly remindedAt: string | null
  /** What the run proposes to change in the brand's plan; set while the run waits at `review-plan`. */
  readonly diff: PlanDiff | null
}

/** A topic as a plan diff names it. */
export interface PlanDiffTopic {
  readonly id: string
  readonly name: string
}

/** A page as a plan diff names it, under the name of its topic. */
export interface PlanDiffPage {
  readonly id: string
  readonly question: string
  readonly topic: string
}

export const PAGE_CHANGE_FIELDS = ['question', 'type', 'stage', 'topic', 'mainKeyword'] as const

export type PageChangeField = (typeof PAGE_CHANGE_FIELDS)[number]

/** A stored page the proposal changes, with the fields that change. */
export interface PlanDiffChangedPage extends PlanDiffPage {
  readonly fields: readonly PageChangeField[]
}

/** A stored topic the proposal renames, with its old name. */
export interface PlanDiffRenamedTopic extends PlanDiffTopic {
  readonly from: string
}

/** What a plan proposal changes in the stored plan. */
export interface PlanDiff {
  readonly topics: {
    readonly added: readonly PlanDiffTopic[]
    readonly removed: readonly PlanDiffTopic[]
    readonly renamed: readonly PlanDiffRenamedTopic[]
  }
  readonly pages: {
    readonly added: readonly PlanDiffPage[]
    readonly removed: readonly PlanDiffPage[]
    readonly changed: readonly PlanDiffChangedPage[]
    readonly kept: readonly PlanDiffPage[]
  }
  readonly keywords: { readonly added: number; readonly removed: number }
}

/** The gate the run waits at, or `null` when it waits at none. */
export function waitingGate(run: PlanRun): PlanGateStage | null {
  if (!isRunWaiting(run)) return null
  return (GATE_STAGES as readonly string[]).includes(run.stage)
    ? (run.stage as PlanGateStage)
    : null
}

export const PLAN_GATE_REFUSAL_REASONS = [
  'plan_gate_missing',
  'plan_gate_answer_invalid',
  'owner_admin_role_required',
] as const

export type PlanGateRefusalReason = (typeof PLAN_GATE_REFUSAL_REASONS)[number]

/** One topic the person keeps at `review-topics`, in the order they set; `name` may differ from the candidate's. */
export interface KeptTopic {
  readonly candidateId: string
  readonly name: string
}

/** The answer to `review-topics`: the topics kept in order, and the topic names the person typed. */
export interface TopicsGateAnswer {
  readonly kept: readonly KeptTopic[]
  readonly added: readonly string[]
}

/** The answer to `review-plan`: the pages the person removes from the plan. */
export interface PlanGateAnswer {
  readonly removedPageIds: readonly string[]
}

/** An answer to the gate it names. */
export type PlanGateReply =
  | { readonly stage: 'review-topics'; readonly answer: TopicsGateAnswer }
  | { readonly stage: 'review-plan'; readonly answer: PlanGateAnswer }

export const PLAN_EDIT_STAGES = ['research', 'competitors', 'review-topics'] as const

export type PlanEditStage = (typeof PLAN_EDIT_STAGES)[number]

/** What a person changed in one stage, `planRuns/{runId}/edits/{stage}`; the stage applies it on top of what it computes. */
export interface PlanStepEdits {
  readonly stage: PlanEditStage
  readonly terms?: { readonly added: readonly string[]; readonly removed: readonly string[] }
  readonly competitors?: { readonly removed: readonly string[] }
  readonly topics?: { readonly added: readonly string[] }
  readonly updatedAt: string | null
}

/** The edits a call sends for one stage. */
export type PlanStepEditsInput = Omit<PlanStepEdits, 'stage' | 'updatedAt'>

export const DIFFICULTIES = ['easy', 'medium', 'hard'] as const

export type Difficulty = (typeof DIFFICULTIES)[number]

/** A topic Raldo proposes at `review-topics`. */
export interface TopicCandidate {
  readonly id: string
  readonly name: string
  readonly whatItCovers: string
  readonly mainSearch: string
  readonly volume: number
  readonly searches: number
  readonly keywordDifficulty: number | null
  readonly difficulty: Difficulty
  /** The names of the beliefs the topic argues. */
  readonly arguments: readonly string[]
  readonly beliefSlugs: readonly string[]
}

export const TERM_SOURCES = ['model', 'added'] as const

export type TermSource = (typeof TERM_SOURCES)[number]

export interface ResearchCoverage {
  readonly id: string
  readonly label: string
  readonly terms: readonly { readonly term: string; readonly source: TermSource }[]
}

export interface ResearchReview {
  readonly stage: 'research'
  readonly coverage: readonly ResearchCoverage[]
  readonly writtenAt: string | null
}

export interface CompetitorsReview {
  readonly stage: 'competitors'
  readonly competitors: readonly {
    readonly name: string
    readonly website: string
    readonly searches: number
    readonly dropped: boolean
  }[]
  readonly writtenAt: string | null
}

export interface DemandReview {
  readonly stage: 'demand'
  readonly measured: number
  readonly perCoverage: readonly {
    readonly id: string
    readonly label: string
    readonly terms: number
    readonly measured: number
  }[]
  readonly writtenAt: string | null
}

export const RELEVANCE_SCORES = [1, 2, 3, 4, 5] as const

export type RelevanceScore = (typeof RELEVANCE_SCORES)[number]

export interface RelevanceReview {
  readonly stage: 'relevance'
  readonly kept: readonly {
    readonly keyword: string
    readonly volume: number
    readonly score: RelevanceScore
    readonly reason: string
    readonly variants: number
  }[]
  readonly dropped: readonly {
    readonly keyword: string
    readonly volume: number
    readonly score: RelevanceScore
    readonly reason: string
  }[]
  readonly writtenAt: string | null
}

export interface TopicCandidatesReview {
  readonly stage: 'topic-candidates'
  readonly candidates: readonly TopicCandidate[]
  readonly writtenAt: string | null
}

export interface ReviewTopicsReview {
  readonly stage: 'review-topics'
  /** The person's answer; `null` while the gate waits for it. */
  readonly answer: TopicsGateAnswer | null
  readonly writtenAt: string | null
}

export interface FillReview {
  readonly stage: 'fill'
  readonly topics: readonly {
    readonly name: string
    readonly terms: number
    readonly measured: number
    readonly kept: number
  }[]
  readonly writtenAt: string | null
}

/** The counts a stage reports plus a short summary, for a stage whose review shows no list. */
interface SummaryReview<Stage extends PlanRunStage, Counts> {
  readonly stage: Stage
  readonly counts: Counts
  readonly lines: readonly string[]
  readonly writtenAt: string | null
}

export type SerpReview = SummaryReview<'serp', SerpCounts>
export type GroupingReview = SummaryReview<'grouping', GroupingCounts>
export type PlacementReview = SummaryReview<'placement', PlacementCounts>
export type DecisionPagesReview = SummaryReview<'decision-pages', DecisionPagesCounts>
export type SiteMatchReview = SummaryReview<'site-match', SiteMatchCounts>

export const LEADS_TO_OFFER_LEVELS = [0, 1, 2, 3] as const

export type LeadsToOfferLevel = (typeof LEADS_TO_OFFER_LEVELS)[number]

export interface TypeStageReview {
  readonly stage: 'type-stage'
  readonly pages: readonly {
    readonly id: string
    readonly question: string
    readonly mainKeyword: string
    readonly keywords: readonly string[]
    readonly volume: number
    readonly type: PageType
    readonly stage: JourneyPhase
    readonly leadsToOffer: LeadsToOfferLevel
    readonly canAnswer: boolean
    readonly reason: string
  }[]
  readonly lines: readonly string[]
  readonly writtenAt: string | null
}

/** A page of the plan a run proposes; its id is the id commit writes. */
export interface ProposedPage {
  readonly id: string
  readonly question: string
  readonly type: PageType
  readonly stage: JourneyPhase
  readonly mainKeyword: string | null
  readonly volume: number
  readonly updatesSitePage: boolean
  readonly decision: boolean
}

/** A topic of the plan a run proposes, with its pages in writing order. */
export interface ProposedTopic {
  readonly id: string
  readonly name: string
  readonly whatItCovers: string
  readonly hubPageId: string
  readonly pages: readonly ProposedPage[]
}

export interface ReviewPlanReview {
  readonly stage: 'review-plan'
  readonly proposal: { readonly topics: readonly ProposedTopic[] }
  /** The person's answer; `null` while the gate waits for it. */
  readonly answer: PlanGateAnswer | null
  readonly writtenAt: string | null
}

export interface CommitReview {
  readonly stage: 'commit'
  readonly topics: number
  readonly pages: number
  readonly keywords: number
  readonly writtenAt: string | null
}

/** What the app shows of one finished stage, `planRuns/{runId}/review/{stage}`. */
export type PlanStageReview =
  | ResearchReview
  | CompetitorsReview
  | DemandReview
  | RelevanceReview
  | TopicCandidatesReview
  | ReviewTopicsReview
  | FillReview
  | SerpReview
  | GroupingReview
  | TypeStageReview
  | PlacementReview
  | DecisionPagesReview
  | SiteMatchReview
  | ReviewPlanReview
  | CommitReview

export type PlanReviewStage = PlanStageReview['stage']

/** The review docs of one run, by stage; a stage that has not finished has none. */
export type PlanReviews = {
  readonly [S in PlanReviewStage]?: Extract<PlanStageReview, { stage: S }>
}

/** The edits docs of one run, by stage. */
export type PlanEdits = { readonly [S in PlanEditStage]?: PlanStepEdits }

/** The reasons the server refuses to open a plan run with. */
export const START_PLAN_REFUSAL_REASONS = [
  'owner_admin_role_required',
  'plan_run_in_progress',
  'plan_focus_needs_plan',
  'brand_domain_unknown',
  'brand_market_undecided',
  'vendor_budget_exhausted',
] as const

export type StartPlanRefusalReason = (typeof START_PLAN_REFUSAL_REASONS)[number]

/** The reasons the server refuses to add a page by hand with. */
export const ADD_PAGE_REFUSAL_REASONS = [
  'editor_role_required',
  'plan_run_in_progress',
  'page_exists',
  'brand_facts_missing',
  'brand_market_undecided',
  'vendor_not_configured',
  'vendor_budget_exhausted',
  'page_not_fit',
  'model_output_invalid',
] as const

export type AddPageRefusalReason = (typeof ADD_PAGE_REFUSAL_REASONS)[number]

/** The longest question and keyword a page added by hand may carry. */
export const ADD_PAGE_LIMITS = { question: 300, keyword: 120 } as const

/** True when the plan of the newest completed run was not made from the current Content Strategy; false when there is no plan. */
export function isPlanStale(
  completedRun: Pick<PlanRun, 'strategyStamp'> | null,
  strategyConfirmedAt: string | null,
): boolean {
  if (completedRun === null) return false
  return completedRun.strategyStamp === null || completedRun.strategyStamp !== strategyConfirmedAt
}

export const INVENTORY_PAGE_SOURCES = ['sitemap', 'ranked', 'home-links'] as const

export type InventoryPageSource = (typeof INVENTORY_PAGE_SOURCES)[number]

/** The brand site as the latest inventory found it, `siteInventory/current`. */
export interface SiteInventory {
  readonly domain: string
  readonly fetchedAt: string | null
  readonly robots: {
    readonly status: number | null
    readonly disallow: readonly string[]
    readonly sitemapUrls: readonly string[]
  }
  readonly sitemaps: readonly string[]
  readonly domainRank: number | null
  readonly rankedKeywords: {
    readonly total: number
    readonly saved: number
    readonly etv: number | null
  }
  readonly counts: InventoryCounts
  readonly contentReadAt: string | null
}

/** One keyword a site page ranks for. */
export interface InventoryRanking {
  readonly keyword: string
  readonly position: number | null
  readonly volume: number | null
  readonly difficulty: number | null
  readonly etv: number | null
}

/** A URL found on the brand site, `inventoryPages/{id}`. */
export interface InventoryPage {
  readonly id: string
  readonly url: string
  readonly sources: readonly InventoryPageSource[]
  readonly lastmod: string | null
  readonly status: number | null
  readonly redirectsTo: string | null
  readonly robotsAllowed: boolean
  readonly noindex: boolean
  readonly canonical: string | null
  readonly title: string | null
  readonly description: string | null
  readonly h1: string | null
  readonly h2s: readonly string[]
  readonly words: number
  readonly rankings: readonly InventoryRanking[]
  readonly firstSeenAt: string | null
  readonly lastSeenAt: string | null
  readonly goneAt: string | null
}

/** Whether a search engine may list the page: answered 200, allowed, not noindex, not redirected. */
export function isIndexable(
  page: Pick<InventoryPage, 'status' | 'robotsAllowed' | 'noindex' | 'redirectsTo'>,
): boolean {
  return page.status === 200 && page.robotsAllowed && !page.noindex && !page.redirectsTo
}

/** A content topic of the plan, `topics/{id}`; its hub Page is named here and nowhere else. */
export interface Topic {
  readonly id: string
  readonly brandId: string
  readonly name: string
  readonly whatItCovers: string
  readonly hubPageId: string
  readonly beliefSlugs: readonly string[]
  readonly position: number
  readonly createdAt: string | null
  readonly updatedAt: string | null
}

/** Whether `pageId` is the hub of `topic`. */
export function isHubPage(topic: Pick<Topic, 'hubPageId'>, pageId: string): boolean {
  return topic.hubPageId === pageId
}

export const SITE_MATCHES = ['ranks', 'covers', 'partly'] as const

export type SiteMatch = (typeof SITE_MATCHES)[number]

/** The brand-site page a planned Page updates. */
export interface ExistingSitePage {
  readonly inventoryPageId: string
  readonly match: SiteMatch
  readonly reason: string
}

/** The belief a belief page argues. */
export interface PageObjective {
  readonly beliefSlug: string | null
  readonly competitor: string | null
}

/** A planned piece of content, `pages/{id}`; status, outcome, source and hub are derived. */
export interface Page {
  readonly id: string
  readonly brandId: string
  readonly topicId: string
  readonly question: string
  readonly type: PageType
  readonly stage: JourneyPhase
  readonly leadsToOffer: 1 | 2 | 3
  readonly reason: string
  readonly writingOrder: number
  readonly existing: ExistingSitePage | null
  readonly objective: PageObjective | null
  readonly createdAt: string | null
  readonly updatedAt: string | null
}

export const PAGE_STATUSES = ['planned', 'site', 'writing', 'live'] as const

export type PageStatus = (typeof PAGE_STATUSES)[number]

/** The status of a Page from its own match and the article written for it, if any, and whether that article is verified live. */
export function pageStatus(
  page: Pick<Page, 'existing'>,
  hasArticle: boolean,
  isLive: boolean,
): PageStatus {
  if (hasArticle && isLive) return 'live'
  if (hasArticle) return 'writing'
  return page.existing !== null && page.existing.match !== 'partly' ? 'site' : 'planned'
}

/** `update` when the Page rewrites a site page, else `new`. */
export function pageOutcome(page: Pick<Page, 'existing'>): 'update' | 'new' {
  return page.existing ? 'update' : 'new'
}

/** `belief` when the Page argues a belief, else `search`. */
export function pageSource(page: Pick<Page, 'objective'>): 'belief' | 'search' {
  return page.objective ? 'belief' : 'search'
}

export const COMPETITION_LEVELS = ['LOW', 'MEDIUM', 'HIGH'] as const

export type CompetitionLevel = (typeof COMPETITION_LEVELS)[number]

/** The vendor's measurement of a keyword. */
export interface KeywordMetrics {
  readonly measuredAt: string | null
  readonly searchVolume: number | null
  readonly monthlySearches: readonly { readonly month: string; readonly volume: number }[]
  readonly trend: {
    readonly monthly: number | null
    readonly quarterly: number | null
    readonly yearly: number | null
  }
  readonly cpc: number | null
  readonly competition: number | null
  readonly competitionLevel: CompetitionLevel | null
  readonly topOfPageBid: { readonly low: number | null; readonly high: number | null }
  readonly difficulty: number | null
  readonly intent: {
    readonly main: SearchIntent | null
    readonly foreign: readonly SearchIntent[]
  }
  readonly coreKeyword: string | null
  readonly wordsCount: number | null
  readonly detectedLanguage: string | null
  readonly categories: readonly number[]
  readonly serp: {
    readonly resultsCount: number | null
    readonly itemTypes: readonly string[]
  } | null
  readonly avgBacklinks: {
    readonly backlinks: number | null
    readonly referringDomains: number | null
    readonly rank: number | null
  } | null
}

export const PAGE_KEYWORD_ROLES = ['main', 'secondary'] as const

export type PageKeywordRole = (typeof PAGE_KEYWORD_ROLES)[number]

/** A search term a Page targets, `pageKeywords/{id}`; a keyword is on one Page at most. */
export interface PageKeyword {
  readonly id: string
  readonly brandId: string
  readonly pageId: string
  readonly keyword: string
  readonly role: PageKeywordRole
  readonly variants: readonly string[]
  readonly metrics: KeywordMetrics
  readonly createdAt: string | null
  readonly updatedAt: string | null
}

/** The `main` keyword of a Page, or null for a belief page. */
export function mainKeywordOf(
  keywords: readonly PageKeyword[],
  pageId: string,
): PageKeyword | null {
  return keywords.find((keyword) => keyword.pageId === pageId && keyword.role === 'main') ?? null
}

/** The monthly searches of a Page: the sum over its keywords, unmeasured ones counting 0. */
export function pageVolume(keywords: readonly PageKeyword[], pageId: string): number {
  return keywords
    .filter((keyword) => keyword.pageId === pageId)
    .reduce((sum, keyword) => sum + (keyword.metrics.searchVolume ?? 0), 0)
}

/** The brand's organic rank for a keyword in one measured week. */
export interface RankPoint {
  readonly week: string
  readonly position: number | null
  readonly url: string | null
}

/** The weekly rank history of one keyword, `keywordRanks/{keywordId}`. */
export interface KeywordRanks {
  readonly keywordId: string
  readonly brandId: string
  readonly points: readonly RankPoint[]
  readonly updatedAt: string | null
}
